import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Slide, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// 1. Create the context for the modal
const ModalContext = createContext({});

// A custom DialogTitle that includes a close button
const CustomDialogTitle = ({ children, onClose, ...other }) => {
  return (
    <DialogTitle sx={{ m: 0, p: 2, pr: 5 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

// 2. Create a custom hook for easy access to modal functions
export const useModal = () => useContext(ModalContext);

// 3. Create the provider component that will render the modal
export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: null,
    actions: null,
    rawContent: null,
    dialogProps: {},
  });

  // Function to show the modal with specific content and configuration
  const showModal = useCallback(({ title, content, actions, rawContent, size, position, ...rest }) => {
    const dialogProps = { ...rest };

    // 1. Handle Size
    if (size) {
      dialogProps.maxWidth = size;
    }

    // 2. Handle Position and Transitions
    const paperSx = { ...(dialogProps.PaperProps?.sx || {}), minHeight: '400px' };
    const containerSx = {};
    const isSlide = ['top', 'bottom', 'left', 'right'].includes(position);

    if (isSlide) {
      const direction = { top: 'down', bottom: 'up', left: 'right', right: 'left' }[position];
      dialogProps.TransitionComponent = Slide;
      dialogProps.TransitionProps = { direction };

      if (position === 'right' || position === 'left') {
        Object.assign(paperSx, {
          margin: 0,
          height: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          width: dialogProps.maxWidth ? undefined : 'auto', // Let maxWidth control width
        });
        containerSx.justifyContent = position === 'right' ? 'flex-end' : 'flex-start';
      } else { // top or bottom
        Object.assign(paperSx, {
          margin: '16px',
          width: `calc(100% - 32px)`, // To respect margin
        });
        containerSx.alignItems = position === 'top' ? 'flex-start' : 'flex-end';
      }
    } else { // Default is 'center'
      dialogProps.TransitionComponent = Fade;
    }

    // 3. Merge styles back into dialogProps
    if (Object.keys(paperSx).length > 0) {
      dialogProps.PaperProps = { ...dialogProps.PaperProps, sx: paperSx };
    }
    if (Object.keys(containerSx).length > 0) {
      dialogProps.sx = { ...dialogProps.sx, '& .MuiDialog-container': containerSx };
    }

    setModalState({
      isOpen: true,
      title,
      content,
      actions,
      rawContent,
      dialogProps,
    });
  }, []);

  // Function to hide the modal
  const hideModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Handle closing the dialog, respecting disable flags
  const handleClose = (event, reason) => {
    if (modalState.dialogProps.disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (modalState.dialogProps.disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    hideModal();
  };

  // Memoize the context value to prevent unnecessary re-renders on consumers
  const value = useMemo(() => ({ showModal, hideModal }), [showModal, hideModal]);

  return (
    <ModalContext.Provider value={value} >
      {children}
      <Dialog
        open={modalState.isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm" // Default size, can be overridden
        {...modalState.dialogProps} 
      >
        {modalState.rawContent ? (
          modalState.rawContent
        ) : (
          <>
            {modalState.title && <CustomDialogTitle onClose={hideModal}>{modalState.title}</CustomDialogTitle>}
            {modalState.content && (
              <DialogContent
                dividers={!!modalState.title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {modalState.content}
              </DialogContent>
            )}
            {modalState.actions && <DialogActions sx={{ p: '16px 24px', justifyContent: 'center' }}>{modalState.actions}</DialogActions>}
          </>
        )}
      </Dialog>
    </ModalContext.Provider>
  );
};